import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const HOURS = Array.from({ length: 15 }, (_, i) => i + 7); // 7 AM to 9 PM

const STATUS_COLORS = {
  draft: 'bg-yellow-50 border-yellow-300 text-yellow-900 dark:bg-yellow-900/20 dark:border-yellow-700 dark:text-yellow-200',
  planned: 'bg-blue-50 border-blue-300 text-blue-900 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-200',
  ready: 'bg-blue-50 border-blue-300 text-blue-900 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-200',
  active: 'bg-green-50 border-green-300 text-green-900 dark:bg-green-900/20 dark:border-green-700 dark:text-green-200',
  completed: 'bg-gray-50 border-gray-300 text-gray-700 dark:bg-gray-700/20 dark:border-gray-600 dark:text-gray-300',
  archived: 'bg-gray-50 border-gray-300 text-gray-500 dark:bg-gray-700/20 dark:border-gray-600 dark:text-gray-400',
};

const STATUS_DOT = {
  draft: 'bg-yellow-400',
  planned: 'bg-blue-400',
  ready: 'bg-blue-400',
  active: 'bg-green-400',
  completed: 'bg-gray-400',
  archived: 'bg-gray-400',
};

function getWeekStart(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatTime(isoString) {
  const d = new Date(isoString);
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return m === 0 ? `${hour} ${ampm}` : `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function formatHour(hour) {
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h = hour % 12 || 12;
  return `${h} ${ampm}`;
}

function isSameDay(date1, date2) {
  return date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate();
}

export default function WeekView({ weekStart, events, courses, onEventClick, onNavigate }) {
  const { t } = useTranslation();
  const today = new Date();
  const start = getWeekStart(weekStart);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });

  const weekLabel = (() => {
    const end = days[6];
    const startMonth = start.toLocaleString('default', { month: 'short' });
    const endMonth = end.toLocaleString('default', { month: 'short' });
    if (start.getMonth() === end.getMonth()) {
      return `${startMonth} ${start.getDate()} – ${end.getDate()}, ${start.getFullYear()}`;
    }
    if (start.getFullYear() !== end.getFullYear()) {
      return `${startMonth} ${start.getDate()}, ${start.getFullYear()} – ${endMonth} ${end.getDate()}, ${end.getFullYear()}`;
    }
    return `${startMonth} ${start.getDate()} – ${endMonth} ${end.getDate()}, ${start.getFullYear()}`;
  })();

  function getEventsForDay(date) {
    return events.filter(e => {
      const s = new Date(e.start);
      return isSameDay(s, date);
    });
  }

  function getCoursesForDay(date) {
    return courses.filter(c => {
      const cs = new Date(c.starts_on);
      const ce = c.ends_on ? new Date(c.ends_on) : null;
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const s = new Date(cs.getFullYear(), cs.getMonth(), cs.getDate());
      if (ce) {
        const e = new Date(ce.getFullYear(), ce.getMonth(), ce.getDate());
        return d >= s && d <= e;
      }
      return isSameDay(d, s);
    });
  }

  // Calculate event position in hour grid
  function eventStyle(event) {
    const startDate = new Date(event.start);
    const startHour = startDate.getHours() + startDate.getMinutes() / 60;
    const duration = event.duration_minutes / 60;
    const top = Math.max(0, (startHour - HOURS[0]) * 60); // px, 60px per hour
    const height = Math.max(30, duration * 60); // min 30px
    return { top: `${top}px`, height: `${height}px` };
  }

  function navigatePrev() {
    const prev = new Date(start);
    prev.setDate(prev.getDate() - 7);
    onNavigate(prev);
  }

  function navigateNext() {
    const next = new Date(start);
    next.setDate(next.getDate() + 7);
    onNavigate(next);
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={navigatePrev}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
          aria-label={t('teacherCalendar.previousWeek')}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {weekLabel}
        </h3>
        <button
          onClick={navigateNext}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
          aria-label={t('teacherCalendar.nextWeek')}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      </div>

      {/* Course bars (all-day) */}
      {(() => {
        const weekCourses = courses.filter(c => {
          const cs = new Date(c.starts_on);
          const ce = c.ends_on ? new Date(c.ends_on) : cs;
          return ce >= days[0] && cs <= days[6];
        });
        if (weekCourses.length === 0) return null;
        return (
          <div className="mb-2 space-y-1">
            {weekCourses.map(course => (
              <div key={course.id} className="flex items-center gap-2 px-3 py-1 rounded-md bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                <span className="w-2 h-2 rounded-full bg-purple-400 flex-shrink-0" />
                <span className="text-xs font-medium text-purple-800 dark:text-purple-200 truncate">{course.title}</span>
              </div>
            ))}
          </div>
        );
      })()}

      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="border-r border-gray-200 dark:border-gray-700" />
          {days.map((day, i) => {
            const isToday = isSameDay(day, today);
            return (
              <div
                key={i}
                className={cn(
                  'text-center py-2 border-r border-gray-200 dark:border-gray-700 last:border-r-0',
                  isToday && 'bg-blue-50 dark:bg-blue-900/20'
                )}
              >
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{DAYS[i]}</div>
                <div className={cn(
                  'text-sm font-semibold mt-0.5 inline-flex items-center justify-center',
                  isToday && 'bg-blue-600 text-white rounded-full w-6 h-6',
                  !isToday && 'text-gray-900 dark:text-white'
                )}>
                  {day.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Time grid */}
        <div className="grid grid-cols-[60px_repeat(7,1fr)] overflow-y-auto max-h-[640px]">
          {/* Hour rows */}
          {HOURS.map((hour) => (
            <div key={hour} className="contents">
              {/* Time label */}
              <div className="h-[60px] border-r border-b border-gray-200 dark:border-gray-700 flex items-start justify-end pr-2 pt-0.5">
                <span className="text-[10px] text-gray-400 dark:text-gray-500 leading-none">{formatHour(hour)}</span>
              </div>
              {/* Day columns */}
              {days.map((day, di) => {
                const isToday = isSameDay(day, today);
                return (
                  <div
                    key={di}
                    className={cn(
                      'h-[60px] border-r border-b border-gray-200 dark:border-gray-700 last:border-r-0 relative',
                      isToday && 'bg-blue-50/30 dark:bg-blue-900/10'
                    )}
                  >
                    {/* Render events that start in this hour */}
                    {getEventsForDay(day)
                      .filter(ev => {
                        const startH = new Date(ev.start).getHours();
                        return startH === hour;
                      })
                      .map((event) => {
                        const startDate = new Date(event.start);
                        const minuteOffset = startDate.getMinutes();
                        const duration = event.duration_minutes || 60;
                        const topPx = minuteOffset; // 1px per minute within the hour cell
                        const heightPx = Math.max(24, duration);
                        return (
                          <button
                            key={event.id}
                            onClick={() => onEventClick && onEventClick(event)}
                            className={cn(
                              'absolute left-0.5 right-0.5 rounded border px-1.5 py-0.5 text-left overflow-hidden z-10 transition-shadow hover:shadow-md',
                              STATUS_COLORS[event.status] || STATUS_COLORS.active
                            )}
                            style={{ top: `${topPx}px`, height: `${heightPx}px` }}
                            title={`${event.title}\n${formatTime(event.start)}${event.end ? ' – ' + formatTime(event.end) : ''}\n${event.location || ''}`}
                          >
                            <div className="flex items-center gap-1">
                              <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', STATUS_DOT[event.status] || STATUS_DOT.active)} />
                              <span className="text-[11px] font-semibold truncate">{event.title}</span>
                            </div>
                            {heightPx > 30 && (
                              <div className="text-[10px] opacity-80 truncate">
                                {formatTime(event.start)}{event.location ? ` · ${event.location}` : ''}
                              </div>
                            )}
                            {heightPx > 50 && event.course_title && (
                              <div className="text-[10px] opacity-70 truncate">{event.course_title}</div>
                            )}
                          </button>
                        );
                      })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
